import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import CustomInput from "@components/Forms/CustomInput";
import OutlineButton from "@components/Buttons/OutlineButton";
import PrimaryButton from "@components/Buttons/PrimaryButton";
import queryKeys from "@configs/react-query/query-key";
import { useMutationData } from "@hooks/useMutationData";
import { useSearchInfo } from "@hooks/useSearchInfo";
import { useTheme } from "@hooks/useTheme";
import { CustomCheckbox } from "@components/Forms";
import { ChangePasswordModal } from "@components/Dialogs";
import ToastConstant from "@configs/react-toastify/snackbar";
import { TOAST } from "@constants/index";
import { formatDateTime, getScanToolHelper, translateDepartment } from "@utils/common";
import UserNameWithId from "@components/UI/UserNameWithId";
import { formatUserNameWithId } from "@utils/userDisplay";
import { getLocalStorage } from "@utils/local-storage";
import { LOCAL_STORAGE } from "@constants/index";
import validator from 'validator';

const MyProfile = () => {
  const navigate = useNavigate();
  const currentUserId = getLocalStorage(LOCAL_STORAGE.USER_ID);
  const isFirstRender = useRef(true);
  const { colors } = useTheme();
  const { t } = useTranslation();

  const [isEditMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);

  const [userData, setUserData] = useState({
    userId: "",
    userName: "",
    roleCd: "",
    roleCdName: "",
    expirationDt: "",
    enableFlag: "",
    deptCd: "",
    deptCdName: "",
    position: "",
    email: "",
    phoneNo: "",
    cellNo: "",
    userMailConfig: {
      binaryVerificationMailFlag: "",
      cveDetectionMailFlag: "",
      securityAlertMailFlag: "",
      sourceCodeVerificationMailFlag: "",
      sbomVerificationMailFlag: "",
      clarityAirVerificationMailFlag: "",
    },
    password: "",
    passwordHash: "",
    companyCd: ""
  });

  const [editFormData, setEditFormData] = useState({
    userId: "",
    userName: "",
    position: "",
    email: "",
    phoneNo: "",
    cellNo: "",
    userMailConfig: {
      binaryVerificationMailFlag: "",
      cveDetectionMailFlag: "",
      securityAlertMailFlag: "",
      sourceCodeVerificationMailFlag: "",
      sbomVerificationMailFlag: "",
      clarityAirVerificationMailFlag: "",
    }
  });
  const { scanToolList, fetchSearchInfo } = useSearchInfo();
  const { hasTool } = getScanToolHelper(scanToolList);

  // Get User Details API
  const getUserDetails = useMutationData(
    queryKeys.getMyProfile,
    {
      method: "GET",
    },
    {
      onSuccess: (response) => {
        if (typeof response !== "string") {
          setUserData(response);
          setEditFormData(response);
        }
      },
      onError: (error) => {
        console.error("Error during get user details:", error);
        ToastConstant(t("user.get-details-error", "Failed to get user details"), TOAST.error);
      },
    },
  );

  // Update User API
  const updateUserData = useMutationData(
    queryKeys.getMyProfile,
    {
      method: "PUT",
    },
    {
      onSuccess: () => {
        getUserDetails.mutate();
        ToastConstant(TOAST.success, t("system.user.user-updated-success"));
        setLoading(false);
        setEditMode(false);
      },
      onError: (error) => {
        console.error("Error during update data:", error);
        const { status, data } = error.response;
        const errorMsg = status === 400 && data ? data : "";
        let subErrMsg = "";
        switch (errorMsg) {
          case "Duplicate email found":
            subErrMsg = t("system.user.add-error-email-duplicate");
            break;
          default:
            subErrMsg = "";
        }
        ToastConstant(TOAST.error, t("user.update-fail"), subErrMsg);
        setLoading(false);
      },
    },
  );

  useEffect(() => {
    if (isFirstRender.current) {
      fetchSearchInfo();
      if (currentUserId) {
        getUserDetails.mutate();
      }
      isFirstRender.current = false;
    }
  }, [fetchSearchInfo, currentUserId, getUserDetails]);

  // Handlers
  const handleInputChange = (field, value) => {
    setEditFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    if (validationErrors[field]) {
      setValidationErrors((prev) => ({ ...prev, [field]: "" }));
    }
    if (field === "userMailConfig") {
      setValidationErrors((prev) => ({ ...prev, email: "" }));
    }
  };

  const handleEditClick = () => {
    setEditFormData(userData);
    setValidationErrors({});
    setEditMode(true);
  };

  const handleCancelEdit = () => {
    setEditFormData(userData);
    setValidationErrors({});
    setEditMode(false);
  };

  const handleChangePassword = () => {
    setShowChangePasswordModal(true);
  };

  const onPasswordChanged = async (newPassword) => {
    getUserDetails.mutate({ userId });
  };

  const isValidEmail = (email) => validator.isEmail(email);
  const isValidPhoneCellNo = (number) => validator.isMobilePhone(number);
  const validateForm = () => {
    const errors = {};

    if (!isValidEmail(editFormData.email) && editFormData.email?.trim()) {
      errors.email = "system.user.email-required";
    }
    if (!isValidPhoneCellNo(editFormData.phoneNo) && editFormData.phoneNo?.trim()) {
      errors.phoneNo = "system.phone-no-invalid";
    }
    if (!isValidPhoneCellNo(editFormData.cellNo) && editFormData.cellNo?.trim()) {
      errors.cellNo = "system.cell-no-invalid";
    }
    if (!editFormData.userName?.trim()) {
      errors.userName = "toast.validation.required";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const isFormValid = () => {
    const baseValid = !!editFormData.userName?.trim();
    const emailValid = !!editFormData.email?.trim() && isValidEmail(editFormData.email);
    const noInvalidPhone =
      (editFormData.phoneNo?.trim() ? isValidPhoneCellNo(editFormData.phoneNo) : true) &&
      (editFormData.cellNo?.trim() ? isValidPhoneCellNo(editFormData.cellNo) : true);
    return baseValid && emailValid && noInvalidPhone;
  };

  const handleSaveEdit = () => {
    if (!isValidEmail(editFormData.email) && editFormData.email?.trim()) {
      ToastConstant(TOAST.error, t("system.user.email-required"));
      return;
    }
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    const bodyRequest = {
      ...userData,
      userId: editFormData.userId,
      userName: editFormData.userName.trim(),
      position: editFormData.position || "",
      email: editFormData.email || "",
      phoneNo: editFormData.phoneNo || "",
      cellNo: editFormData.cellNo || "",
      userMailConfig: {
        binaryVerificationMailFlag: editFormData.userMailConfig.binaryVerificationMailFlag,
        cveDetectionMailFlag: editFormData.userMailConfig.cveDetectionMailFlag,
        securityAlertMailFlag: editFormData.userMailConfig.securityAlertMailFlag,
        sourceCodeVerificationMailFlag: editFormData.userMailConfig.sourceCodeVerificationMailFlag,
        sbomVerificationMailFlag: editFormData.userMailConfig.sbomVerificationMailFlag,
        clarityAirVerificationMailFlag: editFormData.userMailConfig.clarityAirVerificationMailFlag,
      },
    };

    updateUserData.mutate(bodyRequest);
  };

  const getMailReceptionTags = () => {
    const tags = [];
    if (hasTool("clarity") && userData.userMailConfig?.binaryVerificationMailFlag === "Y") {
      tags.push(t("system.user.clarity-verif-mail-reception", "Clarity Verif. Mail Reception"));
    }
    if (userData.userMailConfig?.cveDetectionMailFlag === "Y") {
      tags.push(t("system.user.cve-detection-mail-reception", "CVE Detection"));
    }
    if (userData.userMailConfig?.securityAlertMailFlag === "Y") {
      tags.push(t("system.user.security-alert-mail-reception", "Security Alert"));
    }
    if (hasTool("fossid") && userData.userMailConfig?.sourceCodeVerificationMailFlag === "Y") {
      tags.push(t("system.user.fossid-verif-mail-reception", "FossID Verif. Mail Reception"));
    }
    if (hasTool("sbom") && userData.userMailConfig?.sbomVerificationMailFlag === "Y") {
      tags.push(t("system.user.sbom-verif-mail-reception", "SBOM Verif. Mail Reception"));
    }
    if (hasTool("clarityair") && userData.userMailConfig?.clarityAirVerificationMailFlag === "Y") {
      tags.push(t("system.user.clarity-air-verif-mail-reception", "ClarityAIR Verif. Mail Reception"));
    }
    return tags;
  };

  return (
    <div data-testid="my-profile-page">
      {/* Header */}
      <div className={`${colors.bgSecondary} ${colors.borderPrimary}`}>
        <div className="flex items-center justify-between">
          <div className="items-center space-x-4">
            <h1 className={`${colors.textPrimary} text-[28px] font-bold uppercase`}>
              {t("profile.profile-setting")}
            </h1>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className={`mt-5 p-6 ${colors.bgPrimary} rounded-2xl`} data-testid="my-profile-form">
        <div>
          {/* User Information Section */}
          <div className="mb-6">
            <div className="flex items-center justify-between pb-4">
              <h2 className={`${colors.textPrimary} text-xl font-semibold uppercase`}>
                {isEditMode ? t("profile.edit-my-acc-info") : t("profile.my-acc-info")}
              </h2>
              {isEditMode ? (
                <div className="flex items-center space-x-3">
                  <OutlineButton onClick={handleCancelEdit}>
                    {t("common.cancel-button", "Cancel")}
                  </OutlineButton>
                  <PrimaryButton
                    data-testid="my-profile-save-button"
                    onClick={handleSaveEdit}
                    disabled={!isFormValid() || loading}
                  >
                    {loading ? (
                      <div className="flex items-center">
                        <div className="mr-2 h-4 w-4 animate-spin rounded-full border-b-2 border-white"></div>
                        {t("common.saving", "Saving")}
                      </div>
                    ) : (
                      t("common.save-button", "Save")
                    )}
                  </PrimaryButton>
                </div>
              ) : (
                <OutlineButton onClick={handleEditClick}>
                  {t("profile.edit-information", "Edit Information")}
                </OutlineButton>
              )}
            </div>

            {/* Content Grid */}
            <div className="space-y-2 px-4">
              {/* Row 1 - Read-only information */}
              <div className={`grid grid-cols-5 gap-x-6 gap-y-4 border-t ${colors.borderSection} pt-3`}>
                <div className="min-w-0">
                  <label className={`block text-sm font-medium ${colors.textSecondary} mb-1`}>
                    {t("common.creator", "Creator")}
                  </label>
                  {isEditMode ? (
                    <CustomInput
                      type="text"
                      value={formatUserNameWithId(userData.createrName, userData.createrId)}
                      disabled
                    />
                  ) : (
                    <div className="min-h-10 flex items-center min-w-0">
                      <UserNameWithId name={userData.createrName} id={userData.createrId} layout="inline" />
                    </div>
                  )}
                </div>

                <div>
                  <label className={`block text-sm font-medium ${colors.textSecondary} mb-1`}>
                    {t("common.create-at", "Creation Date")}
                  </label>
                  {isEditMode ? (
                    <CustomInput
                      type="text"
                      value={formatDateTime(userData.createdAt)}
                      disabled
                    />
                  ) : (
                    <p className={`text-sm ${colors.textPrimary} h-10 flex items-center`}>
                      {formatDateTime(userData.createdAt)}
                    </p>
                  )}
                </div>

                <div>
                  <label className={`block text-sm font-medium ${colors.textSecondary} mb-1`}>
                    {t("common.last-modified-by")}
                  </label>
                  {isEditMode ? (
                    <CustomInput
                      type="text"
                      value={formatUserNameWithId(userData.updaterName, userData.updaterId)}
                      disabled
                    />
                  ) : (
                    <div className="min-h-10 flex items-center min-w-0">
                      <UserNameWithId name={userData.updaterName} id={userData.updaterId} layout="inline" />
                    </div>
                  )}
                </div>

                <div>
                  <label className={`block text-sm font-medium ${colors.textSecondary} mb-1`}>
                    {t("common.last-modified-date")}
                  </label>
                  {isEditMode ? (
                    <CustomInput
                      type="text"
                      value={formatDateTime(userData.updatedAt)}
                      disabled
                    />
                  ) : (
                    <p className={`text-sm ${colors.textPrimary} h-10 flex items-center`}>
                      {formatDateTime(userData.updatedAt)}
                    </p>
                  )}
                </div>

                {/* <div>
                  <label className={`block text-sm font-medium ${colors.textSecondary} mb-1`}>
                    {t("profile.creator-department", "Creator Department")}
                  </label>
                  {isEditMode ? (
                    <CustomInput
                      type="text"
                      value={translateDepartment(t, userData.deptCdName) || "-"}
                      disabled
                    />
                  ) : (
                    <div className="h-10 flex items-center min-w-0">
                      <p
                        title={translateDepartment(t, userData.deptCdName) || "-"}
                        className={`text-sm ${colors.textPrimary} truncate`}>
                        {translateDepartment(t, userData.deptCdName) || "-"}
                      </p>
                    </div>
                  )}
                </div> */}

                <div>
                  <label className={`block text-sm font-medium ${colors.textSecondary} mb-1`}>
                    {t("common.user-id", "User ID")}
                    {isEditMode && (<span className={`ml-1 ${colors.textRequired}`}>*</span>)}
                  </label>
                  {isEditMode ? (
                    <CustomInput
                      type="text"
                      value={editFormData.userId || ""}
                      onChange={(e) => handleInputChange("userId", e.target.value)}
                      disabled
                    />
                  ) : (
                    <div className="h-10 flex items-center min-w-0">
                      <p
                        title={userData.userId}
                        className={`text-sm ${colors.textPrimary} truncate`}>
                        {userData.userId}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Row 2 - Editable fields */}
              <div className={`grid grid-cols-5 gap-x-4 gap-y-4 pt-3 ${!isEditMode ? `border-t ${colors.borderSection}` : ""}`}>
                <div>
                  <label className={`block text-sm font-medium ${colors.textSecondary} mb-1`}>
                    {t("common.user-name", "User Name")}
                    {isEditMode && (<span className={`ml-1 ${colors.textRequired}`}>*</span>)}
                  </label>
                  {isEditMode ? (
                    <CustomInput
                      type="text"
                      value={editFormData.userName || ""}
                      onChange={(e) => handleInputChange("userName", e.target.value)}
                    />
                  ) : (
                    <div className="h-10 flex items-center min-w-0">
                      <p
                        title={userData.userName}
                        className={`text-sm ${colors.textPrimary} truncate`}>
                        {userData.userName}
                      </p>
                    </div>
                  )}
                  {validationErrors.userName && (
                    <p className={`${colors.textDanger} text-xs mt-1`}>{t(validationErrors.userName)}</p>
                  )}
                </div>

                <div>
                  <label className={`block text-sm font-medium ${colors.textSecondary} mb-1`}>
                    {t("system.user.position", "Position")}
                  </label>
                  {isEditMode ? (
                    <CustomInput
                      type="text"
                      value={editFormData.position || ""}
                      onChange={(e) => handleInputChange("position", e.target.value)}
                      placeholder={t("system.user.position-placeholder")}
                    />
                  ) : (
                    <div className="h-10 flex items-center min-w-0">
                      <p
                        title={userData.position}
                        className={`text-sm ${colors.textPrimary} truncate`}>
                        {userData.position || "---"}
                      </p>
                    </div>
                  )}
                </div>

                <div>
                  <label className={`block text-sm font-medium ${colors.textSecondary} mb-1`}>
                    {t("common.email", "Email")}
                    {isEditMode && <span className={`ml-1 ${colors.textRequired}`}>*</span>}
                  </label>
                  {isEditMode ? (
                    <CustomInput
                      type="email"
                      value={editFormData.email || ""}
                      onChange={(e) => handleInputChange("email", e.target.value)}
                    />
                  ) : (
                    <div className="h-10 flex items-center min-w-0">
                      <p
                        title={userData.email}
                        className={`text-sm ${colors.textPrimary} truncate`}>
                        {userData.email || "---"}
                      </p>
                    </div>
                  )}
                  {validationErrors.email && (
                    <p className={`${colors.textDanger} text-xs mt-1`}>{t(validationErrors.email)}</p>
                  )}
                </div>

                <div>
                  <label className={`block text-sm font-medium ${colors.textSecondary} mb-1`}>
                    {t("system.user.phone-no", "Phone No.")}
                  </label>
                  {isEditMode ? (
                    <CustomInput
                      type="tel"
                      value={editFormData.phoneNo || ""}
                      onChange={(e) => handleInputChange("phoneNo", e.target.value)}
                      placeholder={t("system.user.phone-no-placeholder")}
                    />
                  ) : (
                    <p className={`text-sm ${colors.textPrimary} h-10 flex items-center`}>
                      {userData.phoneNo || "---"}
                    </p>
                  )}
                  {validationErrors.phoneNo && (
                    <p className={`${colors.textDanger} text-xs mt-1`}>{t(validationErrors.phoneNo)}</p>
                  )}
                </div>
                <div>
                  <label className={`block text-sm font-medium ${colors.textSecondary} mb-1`}>
                    {t("system.user.cell-no", "Cell. No.")}
                  </label>
                  {isEditMode ? (
                    <CustomInput
                      type="tel"
                      value={editFormData.cellNo || ""}
                      onChange={(e) => handleInputChange("cellNo", e.target.value)}
                      placeholder={t("system.user.cell-no-placeholder")}
                    />
                  ) : (
                    <p className={`text-sm ${colors.textPrimary} h-10 flex items-center`}>
                      {userData.cellNo || "---"}
                    </p>
                  )}
                  {validationErrors.cellNo && (
                    <p className={`${colors.textDanger} text-xs mt-1`}>{t(validationErrors.cellNo)}</p>
                  )}
                </div>
              </div>

              {/* Row 3 - More editable fields and mail reception */}
              <div className={`grid grid-cols-5 gap-x-4 gap-y-4 pt-3 ${!isEditMode ? `border-t ${colors.borderSection}` : ""}`}>
                <div className="col-span-4">
                  <label className={`block text-sm font-medium ${colors.textSecondary} mb-1`}>
                    {t("system.user.mail-reception", "Mail Reception")}
                  </label>
                  {isEditMode ? (
                    <div className="flex max-w-max flex-col space-y-3">
                      {hasTool("clarity") && <CustomCheckbox
                        id="binaryVerificationMailFlag"
                        checked={editFormData.userMailConfig?.binaryVerificationMailFlag === "Y"}
                        onChange={(e) => handleInputChange("userMailConfig", {
                          ...editFormData.userMailConfig,
                          binaryVerificationMailFlag: e.target.checked ? "Y" : "N"
                        })}
                        label={t("system.user.clarity-verif-mail-reception", "Clarity Verif. Mail Reception")}
                      />}
                      <CustomCheckbox
                        id="cveDetectionMailFlag"
                        checked={editFormData.userMailConfig?.cveDetectionMailFlag === "Y"}
                        onChange={(e) => handleInputChange("userMailConfig", {
                          ...editFormData.userMailConfig,
                          cveDetectionMailFlag: e.target.checked ? "Y" : "N"
                        })}
                        label={t("system.user.cve-detection-mail-reception", "CVE Detection")}
                      />
                      <CustomCheckbox
                        id="securityAlertMailFlag"
                        checked={editFormData.userMailConfig?.securityAlertMailFlag === "Y"}
                        onChange={(e) => handleInputChange("userMailConfig", {
                          ...editFormData.userMailConfig,
                          securityAlertMailFlag: e.target.checked ? "Y" : "N"
                        })}
                        label={t("system.user.security-alert-mail-reception", "Security Alert")}
                      />
                      {hasTool("fossid") && <CustomCheckbox
                        id="sourceCodeVerificationMailFlag"
                        checked={editFormData.userMailConfig?.sourceCodeVerificationMailFlag === "Y"}
                        onChange={(e) => handleInputChange("userMailConfig", {
                          ...editFormData.userMailConfig,
                          sourceCodeVerificationMailFlag: e.target.checked ? "Y" : "N"
                        })}
                        label={t("system.user.fossid-verif-mail-reception", "FossID Verif. Mail Reception")}
                      />}
                      {hasTool("sbom") && <CustomCheckbox
                        id="sbomVerificationMailFlag"
                        checked={editFormData.userMailConfig?.sbomVerificationMailFlag === "Y"}
                        onChange={(e) => handleInputChange("userMailConfig", {
                          ...editFormData.userMailConfig,
                          sbomVerificationMailFlag: e.target.checked ? "Y" : "N"
                        })}
                        label={t("system.user.sbom-verif-mail-reception", "SBOM Verif. Mail Reception")}
                      />}
                      {hasTool("clarityair") && <CustomCheckbox
                        id="clarityAirVerificationMailFlag"
                        checked={editFormData.userMailConfig?.clarityAirVerificationMailFlag === "Y"}
                        onChange={(e) => handleInputChange("userMailConfig", {
                          ...editFormData.userMailConfig,
                          clarityAirVerificationMailFlag: e.target.checked ? "Y" : "N"
                        })}
                        label={t("system.user.clarity-air-verif-mail-reception", "ClarityAIR Verif. Mail Reception")}
                      />}
                    </div>
                  ) : (
                    <div className="h-10 flex items-center">
                      <div className="flex flex-wrap gap-2">
                        {getMailReceptionTags().map((tag, index) => (
                          <span
                            key={index}
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colors.badgeRoleChip}`}
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>


        </div>
      </div>

      <div className={`mt-5 p-6 ${colors.bgPrimary} rounded-2xl`}>
        {/* Password Section */}
        <div className="mb-6">
          <div className="flex items-center justify-between pb-4">
            <h2 className={`${colors.textPrimary} text-xl font-semibold`}>
              {t("system.user.password", "PASSWORD")}
            </h2>
          </div>

          <div className="space-y-2 px-4 sm:px-0">
            <div className="grid grid-cols-5 gap-x-4 gap-y-4">
              <div className="col-span-2">
                <div className="flex items-center gap-4">
                  <div className="flex-1">
                    <CustomInput
                      type="password"
                      value="••••••••••"
                      disabled
                      className={`w-full h-10 rounded-lg border px-3 py-2 text-sm font-medium ${colors.borderPrimary} ${colors.bgInput} ${colors.textPrimary} cursor-default`}
                    />
                  </div>
                  <div className="flex items-end">
                    <OutlineButton onClick={handleChangePassword}>
                      {t("system.user.change-password", "Change password")}
                    </OutlineButton>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Change Password Modal */}
      <ChangePasswordModal
        isOpen={showChangePasswordModal}
        onClose={() => setShowChangePasswordModal(false)}
        user={userData}
        onPasswordChanged={onPasswordChanged}
      />
    </div>
  );
};

export default MyProfile;
